import React, { useState, useEffect } from "react";
// 直接具体路径，不走 components barrel，否则会形成 components ↔ 本文件 的自环
import { Form, FormItem } from "../Base/Form";
import { Button } from "../Base/Button";
import { Dialog } from "../Base/Dialog";
import { Toast } from "../Base/Toast";
import { EnhancedInput } from "../Base/Input/EnhancedInput/EnhancedInput";
import CountryCodeSelect from "../Base/CountrySelect/CountryCodeSelect";
import {
  ResetPasswordDialogProps,
  ResetPasswordFormValues,
} from "./ResetPasswordDialog.types";
import { useT } from "../../i18n/hooks";
import { useCountdown } from "../../hooks/useUtils";
import { resetPassword, sendForgetPhoneCode } from "../../api/auth";
import { processPhone, getPhonePattern } from "../../utils/phoneValidation";
import utils from "../../utils";
import "./ResetPasswordDialog.scss";

/** 从全局配置获取当前国家对应的国家码，与注册页逻辑一致 */
function getDefaultCountryAndCode(): { countryId: string; phoneCode: string } {
  const countryId = utils.getObject("countryId") || "PH";
  const country = window._config?.countryList?.find(
    (c: { id: string; phoneCode?: string }) => c.id === countryId
  );
  if (country?.phoneCode) {
    return { countryId: country.id, phoneCode: country.phoneCode };
  }
  return { countryId: "PH", phoneCode: "+63" };
}

const TOO_MANY_ATTEMPTS_MESSAGE = "Too many password attempts";

/** send_forget_phone_code / 重置密码相关接口返回的 message 与 i18n key 的映射 */
const SEND_FORGET_PHONE_CODE_ERROR_KEYS: Record<string, string> = {
  "user not exists": "auth.errorUserNotExists",
  "Phone number already in use. Please check or try another recovery method.":
    "auth.errorPhoneAlreadyInUse",
};

export const ResetPasswordDialog: React.FC<ResetPasswordDialogProps> = ({
  visible,
  onClose,
  onSuccess,
  onTooManyAttempts,
}) => {
  const t = useT();
  const [form] = Form.useForm<ResetPasswordFormValues>();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState<string>("PH");
  const [selectedPhoneCode, setSelectedPhoneCode] = useState<string>("+63");
  const [formErrors, setFormErrors] = useState<
    Partial<Record<keyof ResetPasswordFormValues, string>>
  >({});

  const {
    timeLeft: countdown,
    isActive: isRunning,
    start,
    reset,
  } = useCountdown(60);

  // 手机号格式校验随所选国家变化，与注册页一致（config/phoneValidation 按 countryId 配置正则）
  const phonePattern = getPhonePattern(selectedCountryId);

  // 当弹窗打开时初始化数据（国家码与注册页一致：从 utils.getObject('countryId') + countryList 读取）
  useEffect(() => {
    if (visible) {
      const { countryId, phoneCode } = getDefaultCountryAndCode();
      form.resetFields();
      setLoading(false);
      setShowPassword(false);
      setSelectedCountryId(countryId);
      setSelectedPhoneCode(phoneCode);
      setFormErrors({});
      reset();
    }
  }, [visible, form, reset]);

  // 发送验证码
  const handleGetSmsCode = async () => {
    try {
      const mobile = form.getFieldValue("mobile");
      if (!mobile) {
        form.setFields([
          { name: "mobile", errors: [t("auth.pleaseEnterMobile")] },
        ]);
        return;
      }

      const fullPhone = `${selectedPhoneCode}${mobile}`;
      if (!phonePattern.test(fullPhone)) {
        form.setFields([
          { name: "mobile", errors: [t("auth.pleaseEnterValidMobile")] },
        ]);
        return;
      }

      const processedPhone = processPhone(fullPhone);
      await sendForgetPhoneCode(processedPhone);

      // 先 reset 把 timeLeft 设回 60，再 start 开始倒计时（只调 start 会从 0 开始）
      reset();
      start();
    } catch (error: any) {
      console.error("Failed to send SMS:", error);
      const rawMessage = (error?.message ?? "").trim();
      if (
        rawMessage === TOO_MANY_ATTEMPTS_MESSAGE ||
        rawMessage.includes(TOO_MANY_ATTEMPTS_MESSAGE)
      ) {
        onClose();
        onTooManyAttempts?.();
        return;
      }
      const title =
        rawMessage && SEND_FORGET_PHONE_CODE_ERROR_KEYS[rawMessage]
          ? t(SEND_FORGET_PHONE_CODE_ERROR_KEYS[rawMessage])
          : rawMessage || t("auth.sendCodeFailed");
      Toast.show({
        type: "failed",
        title,
      });
    }
  };

  // 重置密码
  const handleResetPassword = async (values: ResetPasswordFormValues) => {
    try {
      setLoading(true);
      setFormErrors({});

      const fullPhone = `${selectedPhoneCode}${values.mobile}`;
      const processedPhone = processPhone(fullPhone);

      await resetPassword({
        phone: processedPhone,
        code: values.code,
        password: values.password,
        countryId: selectedCountryId,
      });

      Toast.success(t("auth.resetPasswordSuccess"));
      form.resetFields();
      (onSuccess ?? onClose)();
    } catch (error: any) {
      const message = (error?.message ?? "").trim();
      if (
        message === TOO_MANY_ATTEMPTS_MESSAGE ||
        message.includes(TOO_MANY_ATTEMPTS_MESSAGE)
      ) {
        onClose();
        onTooManyAttempts?.();
        return;
      }
      const title =
        message && SEND_FORGET_PHONE_CODE_ERROR_KEYS[message]
          ? t(SEND_FORGET_PHONE_CODE_ERROR_KEYS[message])
          : message || t("auth.resetPasswordFailed");
      Toast.show({
        type: "failed",
        title,
      });
    } finally {
      setLoading(false);
    }
  };

  // 处理表单字段变化
  const handleFieldsChange = () => {
    const errors = form.getFieldsError();
    const newErrors: Partial<Record<keyof ResetPasswordFormValues, string>> =
      {};

    errors.forEach(field => {
      const fieldName = field.name[0] as keyof ResetPasswordFormValues;
      if (field.errors.length > 0) {
        newErrors[fieldName] = field.errors[0];
      }
    });

    setFormErrors(newErrors);
  };

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      className="reset-password-dialog"
      showCloseButton={true}
      closeButtonPosition="bottom-center"
    >
      <div className="reset-password-dialog__title">
        {t("auth.resetPassword")}
      </div>
      <div className="reset-password-dialog__desc">
        {t("auth.resetPasswordDesc")}
      </div>
      <div
        className="reset-password-dialog__form-wrap"
        style={
          {
            // 内联覆盖 antd-mobile 变量，确保无白底（不依赖 CSS 加载顺序）
            "--adm-color-background": "transparent",
            background: "transparent",
            backgroundColor: "transparent",
          } as React.CSSProperties
        }
      >
        <Form
          form={form}
          onFinish={handleResetPassword}
          onFieldsChange={handleFieldsChange}
          className="reset-password-dialog__form"
        >
          {/* 手机号输入 */}
          <FormItem
            name="mobile"
            rules={[
              {
                required: true,
                message: t("auth.pleaseEnterMobile"),
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const fullPhone = `${selectedPhoneCode}${value}`;
                  if (!phonePattern.test(fullPhone)) {
                    return Promise.reject(
                      new Error(t("auth.pleaseEnterValidMobile"))
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <EnhancedInput
              className="reset-password-dialog__phone-input"
              placeholder={t("auth.pleaseInputYourPhone")}
              autoComplete="tel"
              prefixIcon={{
                isComplexComponent: true,
                icon: (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <CountryCodeSelect
                      value={selectedCountryId}
                      onChange={(countryId, phoneCode) => {
                        setSelectedCountryId(countryId);
                        setSelectedPhoneCode(phoneCode);
                        form.validateFields(["mobile"]);
                      }}
                    />
                    <span
                      style={{
                        color: "var(--color-border-control)",
                        margin: "0 0.5rem 0.375rem 0",
                      }}
                    >
                      |
                    </span>
                  </div>
                ),
                variant: "golden",
              }}
              error={!!formErrors.mobile}
            />
          </FormItem>

          {/* 验证码输入 */}
          <FormItem
            name="code"
            rules={[
              { required: true, message: t("auth.pleaseEnterSms") },
              {
                len: 6,
                message: t("auth.smsCodeLength"),
              },
            ]}
          >
            <EnhancedInput
              placeholder={t("auth.pleaseInputYourCode")}
              maxLength={6}
              suffixContent={{
                text: isRunning
                  ? t("common.resendIn", { time: countdown })
                  : t("auth.send"),
                onClick: handleGetSmsCode,
                loading: false,
                disabled: isRunning,
                variant: "secondary",
                shape: "default",
                size: "sm",
                className: "reset-password-dialog__send-code-btn",
              }}
              error={!!formErrors.code}
            />
          </FormItem>

          {/* 新密码标签 */}
          <div className="reset-password-dialog__label">
            {t("auth.newPassword")}
          </div>

          {/* 新密码输入 */}
          <FormItem
            name="password"
            rules={[
              {
                required: true,
                message: t("auth.pleaseEnterPassword"),
              },
              {
                min: 8,
                message: t("auth.passwordTooShort"),
              },
            ]}
          >
            <EnhancedInput
              placeholder={t("auth.pleaseInputYourPassword")}
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              suffixContent={{
                icon: showPassword ? "eye" : "eye-closed",
                onClick: () => setShowPassword(!showPassword),
              }}
              error={!!formErrors.password}
            />
          </FormItem>

          {/* 重置密码按钮 */}
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            className="reset-password-dialog__submit"
            block
          >
            {t("auth.resetPassword")}
          </Button>
        </Form>
      </div>
    </Dialog>
  );
};
